import { pick, sum } from 'lodash';
import type { ExpressionSpecification, LayerSpecification, Map as MapBox } from 'mapbox-gl';
import { type ColorConfigKey, ColorMap } from '../../SituationalAwareness.types';
import { LatLng, MapBounds } from '@/core/modules/MapManager/MapManager.types';
import { ClusterTypes, ClusterTypesKey, ClusterTypesKeyId } from './MapDataDrawer.types';

/**
 * Not sure it works correctly
 * README: https://stackoverflow.com/questions/68886098/how-can-i-get-the-center-coordinate-from-the-given-coordinates
 */
export const findLocationsCenter = (locations: LatLng[]): { center: Partial<LatLng>; bounds: MapBounds } => {
  const getMiddle = (prop: keyof LatLng, locations: LatLng[]) => {
    const isLongitude = prop === 'longitude';
    let values = locations.map((m) => {
      if (isLongitude && m[prop] < 0) {
        return m[prop] + 360;
      }
      return m[prop];
    });
    let result = sum(values) / values.length;
    if (isLongitude && result > 180) {
      result -= 360;
    }
    return result;
  };

  const getBounds = (): MapBounds => {
    let ne: LatLng = pick(locations[0], ['latitude', 'longitude']);
    let sw: LatLng = pick(locations[0], ['latitude', 'longitude']);
    locations.forEach((spot) => {
      if (spot.latitude > ne.latitude) {
        ne.latitude = Number(spot.latitude);
      }
      if (spot.longitude > ne.longitude) {
        ne.longitude = Number(spot.longitude);
      }
      if (spot.latitude < sw.latitude) {
        //sw = south west
        sw.latitude = Number(spot.latitude);
      }
      if (spot.longitude < sw.longitude) {
        sw.longitude = Number(spot.longitude);
      }
    });

    return { ne, sw };
  };

  const center = {
    latitude: getMiddle('latitude', locations),
    longitude: getMiddle('longitude', locations),
  };

  return {
    center,
    bounds: getBounds(),
  };
};

/**
 * Generates cluster properties and circle colors for incident types.
 * @param colors - An array of colors corresponding to incident types.
 * @param key - The key to use for incident type identification ('incidentTypeId' or 'severityLevel').
 * @returns An object containing cluster properties and circle colors.
 */
export const ClusterTypeColorKey = {
  /**
   * Creates a cluster property key from ColorConfigKey and color identifier
   * Format: type_{ColorConfigKey}_{colorKey}
   * Example: type_IncidentTypeID_1, type_SeverityLevel_critical
   */
  create: (key: ColorConfigKey, idx: ClusterTypesKeyId): keyof ClusterTypes => `type_${key}_${idx}`,
  /**
   * Checks if a string matches the cluster property key format
   */
  is: (key: string): key is keyof ClusterTypes => {
    if (!key.startsWith('type_')) return false;
    const parts = key.split('_');
    if (parts.length !== 3) return false;
    const idxPart = parts[2];
    return Number.isInteger(parseInt(idxPart));
  },
  /**
   * Parses a cluster property key back into its components
   * Returns { colorConfigKey, colorKey } or undefined if invalid
   */
  parse: (key: keyof ClusterTypes | string): ClusterTypesKeyId | undefined => {
    if (!ClusterTypeColorKey.is(key)) return undefined;
    const parts = key.split('_');
    const idxPart = parts[2];
    return parseInt(idxPart).toString();
  },
};

export const generateClusterIncidentProperties = (colors: ColorMap, key: ColorConfigKey) => {
  const colorKeys = Array.from(colors.keys()) satisfies ClusterTypesKeyId[];
  const fallbackColor = 'white';

  const valueExpr: ExpressionSpecification = ['floor', ['to-number', ['get', key]]];

  const buildExpressionSpecifications = (): ExpressionSpecification[] => {
    // Parse color keys to numeric values and sort them
    const numericKeys = colorKeys.map((k) => parseInt(k, 10)).sort((a, b) => a - b);
    const expressions: ExpressionSpecification[] = [];

    for (let i = 0; i < numericKeys.length; i++) {
      const currentValue = numericKeys[i];
      const nextValue = i < numericKeys.length - 1 ? numericKeys[i + 1] : currentValue + 1;

      if (i === 0) {
        // First category: value < nextValue
        expressions[i] = ['<', valueExpr, nextValue];
      } else if (i === numericKeys.length - 1) {
        // Last category: value >= currentValue
        expressions[i] = ['>=', valueExpr, currentValue];
      } else {
        // Middle categories: currentValue <= value < nextValue
        expressions[i] = ['all', ['>=', valueExpr, currentValue], ['<', valueExpr, nextValue]];
      }
    }

    return expressions;
  };

  // Create type expressions based on actual color keys
  // Each type checks if the value equals the specific key
  const expressionSpecs = buildExpressionSpecifications();

  // Build cluster properties from color keys
  const clusterProperties = expressionSpecs.reduce(
    (acc, expression, i) => {
      const colorKey = colorKeys[i];
      const prop = ClusterTypeColorKey.create(key, colorKey);
      acc[prop] = ['+', ['case', expression, 1, 0]];
      return acc;
    },
    {} as Record<ClusterTypesKey, ExpressionSpecification>,
  );

  // Build circle colors expression from color keys and their corresponding colors
  const circleColors: ExpressionSpecification = ['case'];
  for (let i = 0; i < colorKeys.length; i++) {
    const colorKey = colorKeys[i];
    const color = colors.get(colorKey);
    circleColors.push(expressionSpecs[i], color);
  }
  circleColors.push(fallbackColor);

  return { clusterProperties, circleColors };
};

// Normalizes any longitude (e.g. 190, -250, 540) into the standard range [-180, 180).
// This ensures map bounds and incident coordinates are comparable even when Mapbox
// returns longitudes from different "world copies" during horizontal panning.
export const normalizeLng = (lng: number) => ((((lng + 180) % 360) + 360) % 360) - 180; // -> [-180, 180)

export const getLayerMetadata = <T extends object>(map: MapBox, id: string): T | undefined => {
  const layer = map.getLayer(id) as LayerSpecification | undefined;
  return layer?.metadata as T | undefined;
};
